"use client";

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Edit2 } from "lucide-react";
import { useGlobalModalStack } from "@wtc/ui";
import { TagBadgeItem } from "./inline-tags/TagBadgeItem";
import { TagsAddInput } from "./inline-tags/TagsAddInput";
import { HiddenTagsPopover } from "./inline-tags/HiddenTagsPopover";
import { TagsEditorModal } from "./inline-tags/TagsEditorModal";
import {
  useInlineTagsEditorState,
  type TagEntityType,
} from "./inline-tags/useInlineTagsEditorState";

interface InlineTagsEditorProps {
  tags: string[];
  entityId: string;
  entityType: TagEntityType;
  onTagsUpdate: (newTags: string[]) => Promise<void>;
  maxTags?: number;
  maxDisplayed?: number;
  /** When true, show "more tags" and add UI inline instead of in a popover (e.g. on ticket detail) */
  expandInline?: boolean;
  /** Optional modal ID for the tags editor modal */
  globalModalId?: string;
}

export function InlineTagsEditor({
  tags,
  entityId,
  entityType,
  onTagsUpdate,
  maxTags = 20,
  maxDisplayed = 10,
  expandInline = false,
  globalModalId,
}: InlineTagsEditorProps) {
  const globalModalStack = useGlobalModalStack();
  const tagsModalId = globalModalId || `tags-editor-${entityType}-${entityId}`;

  const {
    parsedTags,
    displayedTags,
    hiddenTags,
    hiddenCount,
    isAddingTag,
    newTagInput,
    isLoading,
    popoverOpened,
    setIsAddingTag,
    setNewTagInput,
    setPopoverOpened,
    handleAddTag,
    handleRemoveTag,
    handleKeyPress,
    cancelAddingTag,
  } = useInlineTagsEditorState({ tags, onTagsUpdate, maxTags, maxDisplayed });

  const openTagsModal = useCallback(() => {
    globalModalStack.openModal(tagsModalId);
  }, [globalModalStack, tagsModalId]);

  const closeTagsModal = useCallback(() => {
    globalModalStack.closeModal(tagsModalId);
  }, [globalModalStack, tagsModalId]);

  const togglePopover = useCallback(() => {
    setPopoverOpened(!popoverOpened);
  }, [popoverOpened, setPopoverOpened]);

  if (parsedTags.length === 0 && !isAddingTag) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-500">Tags:</span>
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs gap-1"
          onClick={() => setIsAddingTag(true)}
          disabled={isLoading}
        >
          <Plus className="h-3 w-3" />
          Add tag
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={openTagsModal}
          title="Edit tags in modal"
        >
          <Edit2 className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-500 mr-2">Tags:</span>

        {displayedTags.map((tag) => (
          <TagBadgeItem
            key={tag}
            tag={tag}
            isLoading={isLoading}
            onRemove={(tagToRemove) => void handleRemoveTag(tagToRemove)}
          />
        ))}

        {hiddenCount > 0 &&
          (expandInline ? (
            <div className="mt-2 space-y-1">
              <span className="text-xs font-medium text-gray-500">
                Remaining tags ({hiddenCount})
              </span>
              <div className="flex flex-wrap gap-1">
                {hiddenTags.map((tag) => (
                  <TagBadgeItem
                    key={tag}
                    tag={tag}
                    isLoading={isLoading}
                    onRemove={(tagToRemove) => void handleRemoveTag(tagToRemove)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <HiddenTagsPopover
              hiddenTags={hiddenTags}
              hiddenCount={hiddenCount}
              isLoading={isLoading}
              popoverOpened={popoverOpened}
              onTogglePopover={togglePopover}
              onClosePopover={() => setPopoverOpened(false)}
              onRemove={(tagToRemove) => void handleRemoveTag(tagToRemove)}
            />
          ))}

        {isAddingTag ? (
          <TagsAddInput
            newTagInput={newTagInput}
            isLoading={isLoading}
            onInputChange={setNewTagInput}
            onKeyPress={handleKeyPress}
            onAdd={() => void handleAddTag()}
            onCancel={cancelAddingTag}
          />
        ) : (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs gap-1"
              onClick={() => setIsAddingTag(true)}
              disabled={isLoading || parsedTags.length >= maxTags}
              title={
                parsedTags.length >= maxTags
                  ? `Maximum ${maxTags} tags reached`
                  : "Add a new tag"
              }
            >
              <Plus className="h-3 w-3" />
              Add
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={openTagsModal}
              title="Edit tags in modal"
            >
              <Edit2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      {globalModalStack.isModalOpen(tagsModalId) && (
        <TagsEditorModal
          tagsModalId={tagsModalId}
          entityType={entityType}
          parsedTags={parsedTags}
          maxTags={maxTags}
          isLoading={isLoading}
          newTagInput={newTagInput}
          onInputChange={setNewTagInput}
          onKeyPress={handleKeyPress}
          onAdd={() => void handleAddTag()}
          onCancelAdd={cancelAddingTag}
          onRemove={(tagToRemove) => void handleRemoveTag(tagToRemove)}
          onClose={closeTagsModal}
        />
      )}
    </>
  );
}
